/**
 * Use Cases Section
 * Tabbed interface showing different use cases with cost savings
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { MessageSquare, Code, FileText, BarChart3, TrendingDown, Zap } from 'lucide-react';
import { staggerContainerVariants, itemVariants } from '@/components/animations/variants';

interface UseCase {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  savings: number;
  strategy: string[];
  exampleDecision: {
    prompt: string;
    model: string;
    reasoning: string[];
  };
}

const useCases: UseCase[] = [
  {
    id: 'data-analysis',
    name: 'Data Analysis',
    icon: <BarChart3 className="w-6 h-6" />,
    description: 'Route by analysis depth and data complexity',
    savings: 50,
    strategy: [
      'Simple queries → Gemini Flash ($0.30/1M)',
      'Trend analysis → Claude Haiku ($0.25/1M)',
      'Deep insights → Claude Sonnet ($3.00/1M)',
      'Multi-source synthesis → GPT-5 ($5.00/1M)',
    ],
    exampleDecision: {
      prompt: 'Summarize Q3 sales trends from this CSV',
      model: 'Claude Haiku 4.5',
      reasoning: [
        'Structured data analysis',
        'Standard aggregation task',
        'Clear metrics to extract',
        'Fast processing preferred',
      ],
    },
  },
  {
    id: 'code-generation',
    name: 'Code Generation',
    icon: <Code className="w-6 h-6" />,
    description: 'Match code complexity to model capability',
    savings: 45,
    strategy: [
      'Simple scripts → GPT-5 Mini ($0.40/1M)',
      'Function refactoring → Claude Haiku ($0.25/1M)',
      'Architecture design → Claude Sonnet ($3.00/1M)',
      'System debugging → GPT-5 ($5.00/1M)',
    ],
    exampleDecision: {
      prompt: 'Write a function to validate email addresses',
      model: 'GPT-5 Mini',
      reasoning: [
        'Standard coding task',
        'Well-defined requirements',
        'No complex architecture needed',
        'Fast model sufficient',
      ],
    },
  },
  {
    id: 'content-creation',
    name: 'Content Creation',
    icon: <FileText className="w-6 h-6" />,
    description: 'Optimize for creative quality vs. speed',
    savings: 55,
    strategy: [
      'Social media captions → Gemini Flash ($0.30/1M)',
      'Blog outlines → Claude Haiku ($0.25/1M)',
      'Long-form articles → Claude Sonnet ($3.00/1M)',
      'Brand storytelling → GPT-5 ($5.00/1M)',
    ],
    exampleDecision: {
      prompt: 'Write a tweet about our new product launch',
      model: 'Gemini 2.5 Flash',
      reasoning: [
        'Short-form content',
        'Quick turnaround needed',
        'Straightforward creative task',
        'High volume expected',
      ],
    },
  },
  {
    id: 'customer-support',
    name: 'Customer Support',
    icon: <MessageSquare className="w-6 h-6" />,
    description: 'Route simple FAQs to fast models, complex issues to powerful ones',
    savings: 60,
    strategy: [
      'Simple greetings → Gemini Flash-Lite ($0.15/1M)',
      'FAQs & refunds → Claude Haiku ($0.25/1M)',
      'Complex complaints → Claude Sonnet ($3.00/1M)',
      'Legal escalations → GPT-5 ($5.00/1M)',
    ],
    exampleDecision: {
      prompt: 'How do I reset my password?',
      model: 'Claude Haiku 4.5',
      reasoning: [
        'Simple procedural question',
        'Standard knowledge base query',
        'No complex reasoning required',
        'Fast response needed',
      ],
    },
  },
];

function CostComparisonChart({ savings }: { savings: number }) {
  const data = [
    {
      name: 'Without Pathfinder',
      cost: 100,
      color: '#FF6633', // Orange - hsl(15, 100%, 56%)
    },
    {
      name: 'With Pathfinder',
      cost: 100 - savings,
      color: '#0f7c90', // Darker teal
    },
  ];

  return (
    <div className="w-full h-64 mt-6">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis dataKey="name" />
          <YAxis label={{ value: 'Relative Cost', angle: -90, position: 'insideLeft' }} />
          <Tooltip
            cursor={{ fill: 'transparent' }}
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="backdrop-blur-md bg-background/80 border border-border/50 rounded-lg p-3 shadow-lg">
                    <p className="font-semibold">{payload[0].payload.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Cost: {payload[0].value}% of baseline
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Bar dataKey="cost" radius={[8, 8, 0, 0]} activeBar={false}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function UseCaseContent({ useCase }: { useCase: UseCase }) {
  return (
    <div className="grid md:grid-cols-2 gap-8 mt-8">
      {/* Left Column: Strategy & Example */}
      <div className="space-y-6">
        {/* Routing Strategy */}
        <div>
          <h4 className="text-lg font-semibold mb-3 flex items-center space-x-2">
            <Zap className="w-5 h-5 text-accent" />
            <span>Routing Strategy</span>
          </h4>
          <ul className="space-y-2">
            {useCase.strategy.map((strategy, index) => (
              <li
                key={index}
                className="text-sm text-muted-foreground bg-black/20 backdrop-blur-sm p-3 rounded-lg border border-border/50"
              >
                {strategy}
              </li>
            ))}
          </ul>
        </div>

        {/* Example Decision */}
        <div>
          <h4 className="text-lg font-semibold mb-3">Example Routing Decision</h4>
          <div className="bg-black/20 backdrop-blur-sm p-4 rounded-lg border border-border/50 space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Prompt:</p>
              <p className="text-sm font-mono">&ldquo;{useCase.exampleDecision.prompt}&rdquo;</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Selected Model:</p>
              <p className="text-sm font-semibold text-accent">
                {useCase.exampleDecision.model}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Reasoning:</p>
              <ul className="space-y-1">
                {useCase.exampleDecision.reasoning.map((reason, index) => (
                  <li key={index} className="text-xs text-muted-foreground flex items-start">
                    <span className="mr-2">•</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column: Savings Chart */}
      <div>
        <h4 className="text-lg font-semibold mb-3 flex items-center space-x-2">
          <TrendingDown className="w-5 h-5 text-green-500" />
          <span>Cost Savings: {useCase.savings}%</span>
        </h4>
        <div className="bg-black/20 backdrop-blur-sm p-4 rounded-lg border border-border/50">
          <CostComparisonChart savings={useCase.savings} />
          <p className="text-sm text-muted-foreground text-center mt-4">
            vs. always using premium models (GPT-5 or Claude Sonnet)
          </p>
        </div>
        <Button className="w-full mt-4" variant="outline">
          Try This in Demo →
        </Button>
      </div>
    </div>
  );
}

export function UseCasesSection() {
  const [activeTab, setActiveTab] = useState(useCases[0].id);

  return (
    <section className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Use Cases</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            See how Pathfinder optimizes costs for different workloads
          </p>
        </motion.div>

        {/* Tabbed Interface */}
        <motion.div
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            {/* Tab List */}
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto mb-8">
              {useCases.map((useCase) => (
                <TabsTrigger
                  key={useCase.id}
                  value={useCase.id}
                  className="flex flex-col items-center space-y-2 py-4"
                >
                  <div className="flex items-center space-x-2">
                    {useCase.icon}
                    <span className="hidden sm:inline">{useCase.name}</span>
                    <span className="sm:hidden">{useCase.name.split(' ')[0]}</span>
                  </div>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Tab Content */}
            {useCases.map((useCase) => (
              <TabsContent key={useCase.id} value={useCase.id}>
                <motion.div
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  className="backdrop-blur-md bg-background/50 border border-border/50 rounded-xl p-6 shadow-xl"
                >
                  {/* Description */}
                  <p className="text-lg text-center text-muted-foreground mb-6">
                    {useCase.description}
                  </p>

                  {/* Content */}
                  <UseCaseContent useCase={useCase} />
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
}
